import { FunctionComponent, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import {
  organisationApi,
  OrganisationTag,
  OrganisationTagType,
  useOrganisationQuery,
  useOrganisationTagCountsQuery,
  useOrganisationTagsExternalQuery,
  useOrganisationTagsQuery,
} from '../../../../services';
import {
  Button,
  ButtonType,
  ConfirmationModal,
  FeedFollowBehavior,
  Modal,
  SearchField,
  Select,
  SelectItem,
  TabItem,
  Tabs,
  useDebounce,
  useIsNarrowViewport,
} from '../../../../shared';
import { TagEdit, TagEditResult } from './TagEdit';
import { TagsTable } from './TagsTable';

import classes from './Tags.module.scss';

const TAG_TYPE_TABS = [
  OrganisationTagType.EXTERNAL,
  OrganisationTagType.INTERNAL,
  OrganisationTagType.EXTERNAL_STATUS,
  OrganisationTagType.EVENT,
  OrganisationTagType.USER,
];

const TAGS_PAGE_SIZE = 50;

enum TagErrorType {
  CONFLICT_NAME = 'OrganisationTagConflictNameError',
  REACHED_LIMIT = 'OrganisationTagsReachedLimitError',
}

export const Tags: FunctionComponent = () => {
  const { t } = useTranslation();

  const { t: ctxT } = useTranslation('', { keyPrefix: 'organisationTags' });

  const [organisationTagCreate] = organisationApi.endpoints.organisationTagCreate.useMutation();

  const [organisationTagExternalCreate] =
    organisationApi.endpoints.organisationTagExternalCreate.useMutation();

  const [organisationTagUpdate] = organisationApi.endpoints.organisationTagUpdate.useMutation();

  const [organisationTagExternalUpdate] =
    organisationApi.endpoints.organisationTagExternalUpdate.useMutation();

  const [organisationTagDelete] = organisationApi.endpoints.organisationTagDelete.useMutation();

  const { data: organisation } = useOrganisationQuery();

  const isNarrowViewport = useIsNarrowViewport();

  const allowToManage = Boolean(organisation?.permissions.organisationTagAllowToManage);

  const [activeTypeIndex, setActiveTypeIndex] = useState(0);

  const activeType = TAG_TYPE_TABS[activeTypeIndex];

  const isExternal = activeType === OrganisationTagType.EXTERNAL;

  const [search, setSearch] = useState('');

  const debouncedSearch = useDebounce(search);

  const [page, setPage] = useState(1);

  const [followBehavior, setFollowBehavior] = useState<FeedFollowBehavior>();

  const [tagToEdit, setTagToEdit] = useState<OrganisationTag | null>(null);

  const [tagToDelete, setTagToDelete] = useState<OrganisationTag | null>(null);

  const [isCreateOpen, setIsCreateOpen] = useState(false);

  const { data: counts, refetch: refetchCounts } = useOrganisationTagCountsQuery();

  const {
    data: externalTagsData,
    isFetching: isFetchingExternalTags,
    refetch: refetchExternalTags,
  } = useOrganisationTagsExternalQuery(
    {
      page,
      size: TAGS_PAGE_SIZE,
      filters: {
        query: debouncedSearch,
        ...(followBehavior && { feedFollowBehavior: [followBehavior] }),
      },
    },
    { skip: !isExternal }
  );

  const {
    data: tagsData,
    isFetching: isFetchingTags,
    refetch: refetchTags,
  } = useOrganisationTagsQuery(
    { page, size: TAGS_PAGE_SIZE, filters: { types: [activeType], query: debouncedSearch } },
    { skip: isExternal }
  );

  useEffect(() => {
    if (!isExternal) {
      setFollowBehavior(undefined);
    }
  }, [isExternal]);

  useEffect(() => {
    setPage(1);
  }, [activeType, debouncedSearch, followBehavior]);

  const refetchActiveList = useCallback(() => {
    if (isExternal) {
      refetchExternalTags();
      return;
    }

    refetchTags();
  }, [isExternal, refetchExternalTags, refetchTags]);

  const showTagError = useCallback(
    (errorType?: string) => {
      switch (errorType) {
        case TagErrorType.CONFLICT_NAME:
          return toast.error(ctxT('tag-exists'));
        case TagErrorType.REACHED_LIMIT:
          return toast.error(ctxT('tag-limit'));
        default:
          return toast.error(t('common.error-message'));
      }
    },
    [ctxT, t]
  );

  const onCreate = useCallback(
    async ({ name, type, feedFollowBehavior }: TagEditResult) => {
      const { error } =
        type === OrganisationTagType.EXTERNAL
          ? await organisationTagExternalCreate({ name, feedFollowBehavior }).unwrap()
          : await organisationTagCreate({ name, type }).unwrap();

      if (error) {
        showTagError(error.__typename);
        return;
      }

      setIsCreateOpen(false);
      refetchActiveList();
      refetchCounts();
    },
    [
      organisationTagCreate,
      organisationTagExternalCreate,
      refetchActiveList,
      refetchCounts,
      showTagError,
    ]
  );

  const onEdit = useCallback(
    async ({ name, feedFollowBehavior }: TagEditResult) => {
      if (!tagToEdit) {
        return;
      }

      const { id: tagId, type } = tagToEdit;

      try {
        const { payload, error } =
          type === OrganisationTagType.EXTERNAL
            ? await organisationTagExternalUpdate({ tagId, name, feedFollowBehavior }).unwrap()
            : await organisationTagUpdate({ tagId, name }).unwrap();

        if (!payload) {
          showTagError(error?.__typename);
          return;
        }

        setTagToEdit(null);
        refetchActiveList();
        refetchCounts();
      } catch (_) {
        toast.error(ctxT('tag-empty'));
      }
    },
    [
      ctxT,
      organisationTagExternalUpdate,
      organisationTagUpdate,
      refetchActiveList,
      refetchCounts,
      showTagError,
      tagToEdit,
    ]
  );

  const onRemove = useCallback(async () => {
    if (!tagToDelete) {
      return;
    }

    const payload = await organisationTagDelete({ tagId: tagToDelete.id }).unwrap();

    if (!payload) {
      toast.error(ctxT('delete-error'));
      return;
    }

    setTagToDelete(null);
    refetchActiveList();
    refetchCounts();
  }, [ctxT, organisationTagDelete, refetchActiveList, refetchCounts, tagToDelete]);

  const followBehaviorItems = useMemo<SelectItem<FeedFollowBehavior | 'ALL'>[]>(
    () => [
      { id: 'ALL', title: t('common.all') },
      ...Object.values(FeedFollowBehavior).map((value) => ({
        id: value,
        title: t(`feedFollowBehavior.${value}`),
        hint: t(`feedFollowBehaviorHints.${value}`),
      })),
    ],
    [t]
  );

  const onFollowBehaviorChange = useCallback((id?: FeedFollowBehavior | 'ALL') => {
    setFollowBehavior(id === 'ALL' ? undefined : id);
  }, []);

  const tabItems: TabItem[] = useMemo(
    () =>
      TAG_TYPE_TABS.map((type, index) => ({
        index,
        name: `${ctxT(`title-${type}`)} (${counts?.[type] ?? 0})`,
      })),
    [counts, ctxT]
  );

  return (
    <div className={classes['tags']}>
      <Tabs
        items={tabItems}
        selectedTabIndex={activeTypeIndex}
        setSelectedTabIndex={setActiveTypeIndex}
        scrollable={isNarrowViewport}
        noBackground
      />

      <div className={classes['tags__sub-title']}>{ctxT(`sub-title-${activeType}`)}</div>

      <div className={classes['tags__toolbar']}>
        <SearchField
          value={search}
          onChange={setSearch}
          placeholder={t('common.type-to-search')}
          className={classes['tags__toolbar-search']}
        />

        {isExternal && (
          <div className={classes['tags__toolbar-follow']}>
            <Select
              items={followBehaviorItems}
              onChange={onFollowBehaviorChange}
              selectedItemId={followBehavior ?? 'ALL'}
            />
          </div>
        )}

        {allowToManage && (
          <Button
            type={ButtonType.primary}
            label={ctxT('add-tag')}
            onClick={() => setIsCreateOpen(true)}
            className={classes['tags__toolbar-add']}
          />
        )}
      </div>

      <TagsTable
        tags={isExternal ? externalTagsData?.items ?? [] : tagsData?.items ?? []}
        isFetching={isExternal ? isFetchingExternalTags : isFetchingTags}
        isSearching={Boolean(debouncedSearch)}
        showFollowBehavior={isExternal}
        allowToManage={allowToManage}
        onPageChange={setPage}
        onEdit={setTagToEdit}
        onRemove={setTagToDelete}
        pageInfo={isExternal ? externalTagsData?.pageInfo : tagsData?.pageInfo}
      />

      {isCreateOpen && (
        <Modal
          isOpen={isCreateOpen}
          contentStyle={{ width: '23rem' }}
          body={<TagEdit defaultType={activeType} onSubmit={onCreate} />}
          onClose={() => setIsCreateOpen(false)}
          ariaLabel={ctxT('add-tag')}
        />
      )}

      {tagToEdit && (
        <Modal
          isOpen={Boolean(tagToEdit)}
          contentStyle={{ width: '23rem' }}
          body={<TagEdit tag={tagToEdit} onSubmit={onEdit} />}
          onClose={() => setTagToEdit(null)}
          ariaLabel={ctxT('edit-tag')}
        />
      )}

      <ConfirmationModal
        isOpen={Boolean(tagToDelete)}
        onClose={() => setTagToDelete(null)}
        acceptLabel={t('common.delete')}
        onAccept={onRemove}
        title={t('common.delete-confirmation-title')}
        subTitle={t('common.delete-confirmation-sub-title')}
        contentStyle={{ width: '30rem' }}
        danger
      />
    </div>
  );
};
